import { UserRole } from "@features/settings/types/settings.types";
import { useProfile } from "@shared/hooks/useProfile";
import theme from "@shared/utils/themeColors";
import React from "react";
import { useSupport } from "../context/SupportContext";
import SupportChatWindow from "./SupportChatWindow";

/**
 * Floating support chat bubble for regular users
 */
const SupportChatBubble: React.FC = () => {
  const { user } = useProfile();
  const {
    isChatOpen,
    toggleChat,
    unreadCount,
    createTicket,
    selectedTicketId,
    setSelectedTicketId,
  } = useSupport();

  // Only show chat bubble for regular users (students)
  if (!user || user.role === UserRole.ADMIN) {
    return null;
  }

  // Handle opening the chat and creating a ticket if needed
  const handleOpenChat = async () => {
    if (!isChatOpen) {
      if (!selectedTicketId) {
        const ticketId = await createTicket();
        if (ticketId) {
          setSelectedTicketId(ticketId);
        }
      }
      toggleChat();
    } else {
      toggleChat();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={handleOpenChat}
        className={`fixed bottom-6 right-6 w-14 h-14 rounded-full ${
          theme.bg.brand
        } text-white flex items-center justify-center shadow-lg hover:brightness-105 transition-all z-40 ${
          unreadCount > 0 ? "animate-bounce-gentle" : ""
        }`}
        aria-label="Support Chat"
      >
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}

        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="currentColor"
          viewBox="0 0 16 16"
        >
          <path d="M5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
          <path d="m2.165 15.803.02-.004c1.83-.363 2.948-.842 3.468-1.105A9.06 9.06 0 0 0 8 15c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7c0 1.76.743 3.37 1.97 4.6a10.437 10.437 0 0 1-.524 2.318l-.003.011a10.722 10.722 0 0 1-.244.637c-.079.186.074.394.273.362a21.673 21.673 0 0 0 .693-.125zm.8-3.108a1 1 0 0 0-.287-.801C1.618 10.83 1 9.468 1 8c0-3.192 3.004-6 7-6s7 2.808 7 6c0 3.193-3.004 6-7 6a8.06 8.06 0 0 1-2.088-.272 1 1 0 0 0-.711.074c-.387.196-1.24.57-2.634.893a10.97 10.97 0 0 0 .398-2z" />
        </svg>
      </button>

      {/* Chat Window */}
      {isChatOpen && (
        <div className="fixed bottom-24 right-6 z-50">
          <SupportChatWindow />
        </div>
      )}
    </>
  );
};

export default SupportChatBubble;
